import React, { useEffect, useRef, useState } from "react";
import useBeaconControl from "../../../hooks/useBeaconControl";
import { useTV } from "../../../hooks/useTV";

/**
 * Shared VIP take-control badge (door-2 fireworks views).
 * Self-contained: reads control state via useBeaconControl() directly,
 * so each view only renders <VipTakeControlBadge holdWhile={...} />.
 *
 * Lifecycle mirrors SyncFullscreen's ctrl badge:
 *   hidden → (beat) → pop (sound) → shown → [claim ok: chime]
 *   → granted (green, 2.6s) → slideout → hidden.
 *
 * Colors are copied verbatim from SyncFullscreen.css .sf-ctrl-badge /
 * .sf-ctrl-granted so the two badges are pixel-identical.
 *
 * holdWhile: pass the view's entrance-animation flags (loaders, black
 * overlays, cover images). While ANY are truthy the badge won't pop —
 * and a pending pop beat is CANCELLED if a hold starts during the beat
 * (entrance flags often flip true an effect-tick after mount, so the
 * beat must be revocable or the badge pops over the doors). The beat
 * after a hold RELEASES is longer (700ms + 2000ms): the flags flip
 * false when the door TRANSITION STARTS — the CSS slide runs after — so
 * the plain 700ms would pop over still-moving doors. A mid-show
 * re-steal (no hold involved) keeps the snappy 700ms.
 *
 * TV: capture-phase key interception ONLY while the badge is visible.
 * The badge grabs the remote the moment it pops (green ring); ArrowLeft/
 * Escape hands the remote back to the view's own nav; ArrowRight
 * re-grabs it. While the badge holds the remote, a body class overrides
 * the view buttons' inline green focus ring back to their normal base
 * shadow. The views' nav code is never modified.
 */

const POP_DELAY_MS = 700;
const POST_HOLD_EXTRA_MS = 1000;
const POP_ANIM_MS = 380;
const GRANTED_HOLD_MS = 2600;
const SLIDEOUT_MS = 480;

function VipTakeControlBadge({ holdWhile }) {
  const { isTv } = useTV();
  const {
    isController,
    hasHolder,
    hasAddon,
    synced,
    keyReady,
    claim,
  } = useBeaconControl();

  const [phase, setPhase] = useState("hidden");
  const phaseRef = useRef("hidden");
  phaseRef.current = phase;

  const [remoteFocused, setRemoteFocused] = useState(false);
  const remoteFocusedRef = useRef(false);
  remoteFocusedRef.current = remoteFocused;

  const timersRef = useRef([]);
  const claimingRef = useRef(false);
  const popRef = useRef(null);
  const popTimerRef = useRef(null);
  const chimeRef = useRef(null);

  const timer = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  };

  // Same boot-flash guards as SyncFullscreen, plus hasAddon (claim()
  // would fail no_addon anyway — never show the badge to those accounts).
  const needsControl =
    hasAddon && synced && keyReady && hasHolder && !isController;
  const held = !!holdWhile;
  const prevHeldRef = useRef(false);

  // Drive the badge on/off from live control state. granted/slideout are
  // owned by handleTakeControl — claiming flips needsControl false, and
  // that's success, not dismissal, so don't interrupt those phases here.
  useEffect(() => {
    // Extra beat after a hold releases: the entrance flags flip false when
    // the door TRANSITION STARTS (the CSS slide runs after), so the plain
    // 700ms would pop over the still-moving doors. A mid-show re-steal
    // (no hold involved) keeps the snappy 700ms.
    const justReleased = prevHeldRef.current && !held;
    prevHeldRef.current = held;
    const popDelay = justReleased
      ? POP_DELAY_MS + POST_HOLD_EXTRA_MS
      : POP_DELAY_MS;
    if (needsControl && !held) {
      if (phaseRef.current === "hidden" && !popTimerRef.current) {
        popTimerRef.current = timer(() => {
          popTimerRef.current = null;
          setPhase("pop");
          if (isTv) setRemoteFocused(true);
          try {
            const p = popRef.current;
            if (p) {
              p.volume = 0.7;
              p.currentTime = 0;
              p.play().catch(() => {});
            }
          } catch {}
          timer(() => {
            if (phaseRef.current === "pop") setPhase("shown");
          }, POP_ANIM_MS);
        }, popDelay);
      }
    } else {
      // Doors/overlay started (or control resolved) while the pop beat
      // was still pending — cancel it so the badge can't appear over or
      // before the entrance animation. A fresh beat is scheduled when
      // held goes false again (this effect re-runs on that change).
      if (popTimerRef.current) {
        clearTimeout(popTimerRef.current);
        popTimerRef.current = null;
      }
      if (phaseRef.current === "pop" || phaseRef.current === "shown") {
        setPhase("hidden");
        setRemoteFocused(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsControl, held]);

  useEffect(
    () => () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    },
    []
  );

  // TV: while the remote is on the badge, blur whatever view button held
  // DOM focus and add a body class that overrides the views' green rings
  // back to the buttons' normal base shadow (their focusedButton state
  // can't be cleared from outside — on TV their onBlur is a deliberate
  // no-op). Removed the moment the remote leaves the badge, so the
  // view's ring reappears exactly where their state left it.
  useEffect(() => {
    if (!isTv) return;
    if (remoteFocused) {
      document.body.classList.add("vip-ctrl-has-remote");
      try {
        const a = document.activeElement;
        if (a && a.blur) a.blur();
      } catch {}
    } else {
      document.body.classList.remove("vip-ctrl-has-remote");
    }
    return () => document.body.classList.remove("vip-ctrl-has-remote");
  }, [isTv, remoteFocused]);

  const handleTakeControl = async () => {
    if (claimingRef.current || phaseRef.current === "granted") return;
    if (phaseRef.current !== "pop" && phaseRef.current !== "shown") return;
    claimingRef.current = true;
    try {
      const r = await claim();
      if (r && r.ok) {
        try {
          const c = chimeRef.current;
          if (c) {
            c.volume = 0.5;
            c.currentTime = 0;
            c.play().catch(() => {});
          }
        } catch {}
        setPhase("granted");
        setRemoteFocused(false);
        timer(() => {
          setPhase("slideout");
          timer(() => setPhase("hidden"), SLIDEOUT_MS);
        }, GRANTED_HOLD_MS);
      } else {
        console.error("[beacon] VIP take-control claim failed:", r && r.reason);
      }
    } finally {
      claimingRef.current = false;
    }
  };

  // TV remote interception — mounted ONLY while the badge is interactive,
  // capture phase so it wins over the views' window keydown nav.
  useEffect(() => {
    if (!isTv) return;
    if (phase !== "pop" && phase !== "shown") return;
    const onKey = (e) => {
      const k = e.key;
      if (!remoteFocusedRef.current) {
        // Badge visible but remote not on it: ONLY steal ArrowRight.
        // Everything else falls through to the view's own nav.
        if (k === "ArrowRight") {
          e.preventDefault();
          e.stopPropagation();
          setRemoteFocused(true);
        }
        return;
      }
      // Remote is on the badge.
      if (k === "Enter" || k === " ") {
        e.preventDefault();
        e.stopPropagation();
        handleTakeControl();
        return;
      }
      if (k === "ArrowLeft" || k === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setRemoteFocused(false);
        return;
      }
      // Swallow Up/Down/Tab etc. while the badge holds the remote so the
      // view's focus loop doesn't move underneath it.
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, phase]);

  const interactive = phase === "pop" || phase === "shown";
  const granted = phase === "granted" || phase === "slideout";

  return (
    <>
      {/* Audio elements ALWAYS mounted — the pop sound fires in the same
          tick as setPhase("pop"), before any re-render could mount them. */}
      <audio ref={popRef} preload="auto" src="/assets/doors/peer-pop.mp3" />
      <audio
        ref={chimeRef}
        preload="auto"
        src="/assets/doors/connection-chime.mp3"
      />

      {phase !== "hidden" && (
        <div className="vip-ctrl-wrap">
          <style>{`
            .vip-ctrl-wrap {
              position: fixed;
              right: ${isTv ? "2.4vw" : "24px"};
              bottom: ${isTv ? "3vh" : "24px"};
              z-index: 45; /* under the door/black transition overlays (59/60/998+) */
              font-family: Lato, sans-serif;
              pointer-events: auto;
            }
            .vip-ctrl-badge {
              box-sizing: border-box;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
              min-width: ${isTv ? "26vw" : "380px"};
              min-height: ${isTv ? "7vh" : "64px"};
              max-width: ${isTv ? "44vw" : "86vw"};
              padding: ${isTv ? "1.2vh 1.6vw" : "10px 18px"};
              border-radius: 14px;
              font-size: ${isTv ? "1.35vw" : "18px"};
              font-weight: 800;
              color: #fff;
              text-shadow: 0 1px 4px rgba(0,0,0,0.55);
              background: linear-gradient(180deg, rgba(220, 38, 38, 0.78), rgba(153, 27, 27, 0.82));
              border: 2px solid rgba(254, 202, 202, 0.85);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35), 0 0 18px rgba(220, 38, 38, 0.35);
              cursor: pointer;
            }
            .vip-ctrl-badge.vip-ctrl-granted {
              background: linear-gradient(180deg, rgba(22, 163, 74, 0.85), rgba(21, 128, 61, 0.88));
              border-color: rgba(187, 247, 208, 0.9);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35), 0 0 18px rgba(34, 197, 94, 0.45);
              cursor: default;
            }
            .vip-ctrl-badge.vip-ctrl-pop {
              animation: vipCtrlPopIn ${POP_ANIM_MS}ms ease-out;
            }
            .vip-ctrl-badge.vip-ctrl-slideout {
              animation: vipCtrlSlideOut ${SLIDEOUT_MS}ms ease-in forwards;
            }
            .vip-ctrl-badge.vip-ctrl-remote-focus {
              box-shadow: 0 0 0 6px #3ADF3C, 0 0 18px rgba(220, 38, 38, 0.35), 0 4px 12px rgba(0, 0, 0, 0.35);
            }
            body.vip-ctrl-has-remote .vip_roof-info-button,
            body.vip-ctrl-has-remote .vip_roof-lightbulb-button,
            body.vip-ctrl-has-remote .vip_roof-exit-button,
            body.vip-ctrl-has-remote .vip_hotel-info-button,
            body.vip-ctrl-has-remote .vip_hotel-exit-button {
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4) !important;
              outline: none !important;
            }
            .vip-ctrl-cta {
              display: block;
              color: #ffd93b;
              margin-top: 2px;
              animation: vipCtrlCtaPulse 1.4s ease-in-out infinite;
            }
            @keyframes vipCtrlPopIn {
              0% { opacity: 0; transform: scale(0.7); }
              70% { opacity: 1; transform: scale(1.06); }
              100% { opacity: 1; transform: scale(1); }
            }
            @keyframes vipCtrlSlideOut {
              0% { opacity: 1; transform: translateX(0); }
              100% { opacity: 0; transform: translateX(60vw); }
            }
            @keyframes vipCtrlCtaPulse {
              0%, 100% { text-shadow: 0 0 4px rgba(255,217,59,0.4); opacity: 1; }
              50% { text-shadow: 0 0 14px rgba(255,217,59,0.9); opacity: 0.75; }
            }
          `}</style>
          <div
            role="button"
            className={
              "vip-ctrl-badge vip-ctrl-" +
              phase +
              (granted ? " vip-ctrl-granted" : "") +
              (isTv && remoteFocused ? " vip-ctrl-remote-focus" : "")
            }
            onClick={interactive ? handleTakeControl : undefined}
          >
            {granted ? (
              <span>You're now in control!</span>
            ) : (
              <span>
                This device is NOT controlling your Magic Beacon.{" "}
                <span className="vip-ctrl-cta">
                  {isTv
                    ? "CLICK HERE to take control now!"
                    : "TAP HERE to take control now!"}
                </span>
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VipTakeControlBadge;
